/**
 * GodRaysPass — fullscreen crepuscular-rays / volumetric-light post-pass.
 *
 * Ported from a Shadertoy radial-blur formulation: from each fragment we
 * step toward a "sun" position, accumulating samples of the input texture
 * with an exponential decay. Bright pixels in the source therefore smear
 * outward from the sun, producing classic god-ray shafts.
 *
 * Cost: roughly `samples` extra texture lookups per pixel. With samples=50
 * (Shadertoy default) the cost is significant — keep `strength` at 0 to
 * skip the pass entirely (the renderer disables it when strength is 0).
 *
 * Coordinate convention:
 *   centerX/centerY are 0..1 in viewport UV space (0,0 bottom-left,
 *   1,1 top-right), matching all other pulsefx post-passes. The shader
 *   internally re-centers to -0.5..0.5 with aspect-corrected X to match
 *   the original Shadertoy math.
 *
 * Auto-orbit: when `autoOrbit` is true the renderer drives center from a
 * slow lissajous so there's always motion even without mouse input.
 */

import { ShaderMaterial, Uniform, Vector2 } from "three";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";

export interface GodRaysParams {
  /** Master enable. */
  enabled: boolean;
  /** Wet/dry mix vs. the unmodified input (0..1). */
  strength: number;
  /** Decay per step (0..1). 0.92 = original Shadertoy default. */
  decay: number;
  /** Density factor — how spread out the samples are (0..2). */
  density: number;
  /** Per-sample weight (0..1.5). */
  weight: number;
  /** Sun position in UV (0..1). */
  centerX: number;
  centerY: number;
  /** Number of ray samples; clamped to compile-time NSAMPLES in the shader. */
  samples: number;
}

export const DEFAULT_GOD_RAYS_PARAMS: GodRaysParams = {
  enabled: false,
  strength: 0,
  decay: 0.92,
  density: 1.0,
  weight: 0.58767,
  centerX: 0.5,
  centerY: 0.5,
  samples: 50,
};

const NSAMPLES = 50;

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
precision highp float;

uniform sampler2D tDiffuse;
uniform vec2  uResolution;
uniform float uTime;
uniform vec2  uCenter;     // sun position in UV (0..1)
uniform float uDecay;
uniform float uDensity;
uniform float uWeight;
uniform float uStrength;   // wet/dry
uniform int   uSamples;    // <= NSAMPLES

varying vec2 vUv;

vec4 crepuscular_rays(vec2 texCoords, vec2 pos) {
  vec2  tc            = texCoords;
  vec2  delta         = tc - pos;
  delta              *= (1.0 / float(${NSAMPLES})) * uDensity;
  float decay         = 1.0;

  vec4  color         = texture2D(tDiffuse, tc) * 0.4;

  // Per-pixel jitter so banding becomes noise.
  tc += delta * fract(
    sin(dot(texCoords + fract(uTime), vec2(12.9898, 78.233))) * 43758.5453
  );

  for (int i = 0; i < ${NSAMPLES}; i++) {
    if (i >= uSamples) break;
    tc -= delta;
    vec4 sampl = texture2D(tDiffuse, tc) * 0.4;
    sampl     *= decay * uWeight;
    color     += sampl;
    decay     *= uDecay;
  }
  return color;
}

void main() {
  // Aspect-corrected centered UV (matches original Shadertoy math).
  vec2 uv  = vUv - 0.5;
  uv.x    *= uResolution.x / uResolution.y;
  vec2 pos = uCenter - 0.5;
  pos.x   *= uResolution.x / uResolution.y;

  vec4 rays = crepuscular_rays(uv, pos);
  vec4 base = texture2D(tDiffuse, vUv);
  // Mix the dry base with the (already-base-included) ray accumulation.
  // strength=0 returns the untouched input; strength=1 returns the full
  // radial smear, matching the Shadertoy look.
  gl_FragColor = mix(base, rays, clamp(uStrength, 0.0, 1.0));
}
`;

export class GodRaysPass extends ShaderPass {
  public params: GodRaysParams;

  constructor(width: number, height: number) {
    const material = new ShaderMaterial({
      uniforms: {
        tDiffuse: new Uniform(null),
        uResolution: new Uniform(new Vector2(width, height)),
        uTime: new Uniform(0),
        uCenter: new Uniform(new Vector2(0.5, 0.5)),
        uDecay: new Uniform(DEFAULT_GOD_RAYS_PARAMS.decay),
        uDensity: new Uniform(DEFAULT_GOD_RAYS_PARAMS.density),
        uWeight: new Uniform(DEFAULT_GOD_RAYS_PARAMS.weight),
        uStrength: new Uniform(DEFAULT_GOD_RAYS_PARAMS.strength),
        uSamples: new Uniform(DEFAULT_GOD_RAYS_PARAMS.samples),
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
    });
    super(material, "tDiffuse");
    this.params = { ...DEFAULT_GOD_RAYS_PARAMS };
    this.enabled = false;
  }

  setSize(width: number, height: number): void {
    super.setSize(width, height);
    const u = (this.material as ShaderMaterial).uniforms;
    u.uResolution.value.set(width, height);
  }

  syncUniforms(timeSeconds: number): void {
    const u = (this.material as ShaderMaterial).uniforms;
    u.uTime.value = timeSeconds;
    u.uCenter.value.set(this.params.centerX, this.params.centerY);
    u.uDecay.value = this.params.decay;
    u.uDensity.value = this.params.density;
    u.uWeight.value = this.params.weight;
    u.uStrength.value = this.params.strength;
    u.uSamples.value = Math.max(1, Math.min(NSAMPLES, this.params.samples | 0));
  }

  isActive(): boolean {
    return this.params.enabled && this.params.strength > 0.001;
  }
}
